import { CheckCircle, CurrencyCircleDollar, Warning, XCircle } from '@phosphor-icons/react';
import { formatCurrencyAmounts, getCurrencySymbol } from './helpers';

export const SubscriptionInsights = ({ data, onReview }) => {
  const subs = data.subscriptions || [];
  const verdictConfig = {
    keep: { bg: 'bg-[#d1fae5]', text: 'text-[#065f46]', border: 'border-[#a7f3d0]' },
    review: { bg: 'bg-[#fef3c7]', text: 'text-[#92400e]', border: 'border-[#fde68a]' },
    cancel: { bg: 'bg-[#fecdd3]', text: 'text-[#9f1239]', border: 'border-[#fca5a5]' },
    cancelled: { bg: 'bg-[#f3f4f6]', text: 'text-[#6b7280]', border: 'border-[#e5e7eb]' },
  };

  return (
    <div className="bg-white border border-[#f2f4f6] rounded-2xl overflow-hidden" data-testid="subscription-insights">
      <div className="bg-gradient-to-r from-[#0f172a] to-[#1e293b] p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#f59e0b]/20 flex items-center justify-center">
            <CurrencyCircleDollar size={22} className="text-[#f59e0b]" weight="fill" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Spending Insights</h2>
            <p className="text-[#94a3b8] text-sm">AI-powered recurring expense analysis</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-5">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
            <p className="text-[#94a3b8] text-[10px] uppercase tracking-wider mb-1">Monthly Spend</p>
            {data.total_monthly_by_currency && Object.keys(data.total_monthly_by_currency).length > 0 ? (
              formatCurrencyAmounts(data.total_monthly_by_currency)?.map((amt, i) => (
                <p key={i} className="text-white font-bold text-xl">{amt}</p>
              ))
            ) : (
              <p className="text-white font-bold text-xl">$0</p>
            )}
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
            <p className="text-[#94a3b8] text-[10px] uppercase tracking-wider mb-1">Annual Cost</p>
            {data.total_annual_by_currency && Object.keys(data.total_annual_by_currency).length > 0 ? (
              formatCurrencyAmounts(data.total_annual_by_currency)?.map((amt, i) => (
                <p key={i} className="text-white font-bold text-xl">{amt}</p>
              ))
            ) : (
              <p className="text-white font-bold text-xl">$0</p>
            )}
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
            <p className="text-[#94a3b8] text-[10px] uppercase tracking-wider mb-1">Potential Savings/yr</p>
            {data.potential_savings_by_currency && Object.keys(data.potential_savings_by_currency).length > 0 ? (
              formatCurrencyAmounts(data.potential_savings_by_currency)?.map((amt, i) => (
                <p key={i} className="text-[#4ade80] font-bold text-xl">{amt}</p>
              ))
            ) : (
              <p className="text-[#4ade80] font-bold text-xl">$0</p>
            )}
          </div>
        </div>

        {data.total_potential_savings_line && (
          <p className="text-[#4ade80] text-sm font-semibold mt-4">{data.total_potential_savings_line}</p>
        )}
      </div>

      <div className="p-4 lg:p-6 space-y-3">
        <p className="text-xs text-[#596065] uppercase tracking-wider font-bold mb-2">Recommendations</p>
        {subs.map((sub, i) => {
          const v = verdictConfig[sub.verdict] || verdictConfig.review;
          const sym = getCurrencySymbol(sub.currency);
          const isReviewable = sub.verdict === 'review' || sub.verdict === 'cancel';

          return (
            <div key={i} className={`rounded-xl border ${v.border} p-4 transition-all hover:shadow-sm`} data-testid={`sub-insight-${i}`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-xl ${v.bg} ${v.text} flex items-center justify-center flex-shrink-0 font-bold text-lg`}>
                  {sub.verdict === 'keep' ? <CheckCircle size={20} weight="fill" /> :
                   sub.verdict === 'cancel' || sub.verdict === 'cancelled' ? <XCircle size={20} weight="fill" /> :
                   <Warning size={20} weight="fill" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-bold text-sm ${sub.is_cancelled ? 'text-[#9ca3af]' : 'text-[#2d3338]'}`}>{sub.provider}</span>
                    <span className={`font-semibold text-sm ${sub.is_cancelled ? 'text-[#9ca3af]' : 'text-[#2d3338]'}`}>({sym}{(sub.monthly_cost || 0).toFixed(2)}/mo)</span>
                  </div>
                  <p className="text-sm text-[#596065] mt-1">{sub.usage_line || 'No usage data available'}</p>
                  <p className={`text-sm font-semibold mt-1 ${
                    sub.verdict === 'keep' ? 'text-[#065f46]' :
                    sub.verdict === 'cancel' || sub.verdict === 'cancelled' ? 'text-[#9f1239]' : 'text-[#92400e]'
                  }`}>
                    {sub.verdict === 'keep' && <span>{'\u2713'} </span>}
                    {(sub.verdict === 'cancel' || sub.verdict === 'review') && <span>{'\u2192'} </span>}
                    {sub.action_line || sub.suggestion || ''}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  {sub.annual_savings > 0 && !sub.is_cancelled && (
                    <span className="bg-[#d1fae5] text-[#065f46] text-xs font-bold px-3 py-1 rounded-full">Save {sym}{sub.annual_savings.toFixed(0)}/yr</span>
                  )}
                  {sub.is_cancelled && sub.annual_savings > 0 && (
                    <span className="bg-[#f3f4f6] text-[#6b7280] text-xs font-bold px-3 py-1 rounded-full">Saved {sym}{sub.annual_savings.toFixed(0)}/yr</span>
                  )}
                  {isReviewable && !sub.is_cancelled && (
                    <button onClick={() => onReview(sub.provider)} className="text-xs text-[#005bc1] font-semibold hover:underline whitespace-nowrap" data-testid={`review-btn-${i}`}>
                      I still use this
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {subs.length === 0 && (
          <div className="text-center py-6">
            <CheckCircle size={32} className="mx-auto mb-2 text-[#065f46]" weight="duotone" />
            <p className="text-sm text-[#596065]">All subscriptions reviewed! Looking good.</p>
          </div>
        )}
      </div>
    </div>
  );
};
